import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, onSnapshot, setDoc, Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyCheckDollar
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import swal from 'sweetalert';
import { db } from '../../firebase';
import '../dash.css';
import {
  buildPayrollAllocationPreview,
  buildPayrollRows,
  formatDateTime,
  formatKes,
  getDateTimeInputValue,
  getMonthKey,
  getMonthLabel,
  getPaymentTimestamp,
  isSuccessfulSalaryPayment,
  normalizeAmount,
  resolveSalaryPaymentPeriodKey
} from '../../utils/financeWorkflow';
import { logAdminActivity } from '../../utils/activityLogger';

const LOTUS_API_BASE_URL = process.env.REACT_APP_LOTUS_API_BASE_URL
  || 'https://us-central1-ubunifu-college.cloudfunctions.net/lotusApi';

const normalizeEmployeeStatus = (value) => String(value || 'Active').trim();
const SALARY_METHOD_OPTIONS = ['Bank Transfer', 'MPESA', 'Cash', 'Cheque', 'Other'];
const LOTUS_METHOD_BY_SALARY_METHOD = {
  'Bank Transfer': 'pesalink',
  MPESA: 'mpesa'
};
const LOTUS_SUCCESS_STATUSES = new Set(['paid', 'processed', 'completed', 'successful', 'success', 'succeeded']);
const LOTUS_FAILED_STATUSES = new Set(['failed', 'failure', 'rejected', 'declined', 'cancelled', 'canceled', 'error']);

const normalizePaymentStatus = (value) => String(value || 'Pending').trim();
const upsertSalaryPayment = (payments, payment) => [
  payment,
  ...payments.filter((item) => item.id !== payment.id)
];
const normalizeLotusTransactionId = (value) => String(value || '')
  .trim()
  .replace(/^TR[-_ ]*/i, '');
const generateLotusPaymentTag = () => {
  const uuid = typeof window !== 'undefined'
    && window.crypto
    && typeof window.crypto.randomUUID === 'function'
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return `salary-group-${uuid}`;
};

const findNestedValue = (source, keys) => {
  const queue = [source];
  const seen = new Set();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || typeof current !== 'object' || seen.has(current)) {
      continue;
    }

    seen.add(current);

    for (const key of keys) {
      const value = current[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return value;
      }
    }

    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') {
        queue.push(value);
      }
    }
  }

  return '';
};

const extractLotusPaymentDetails = (payload) => {
  const source = payload?.data?.transaction
    || payload?.data
    || payload?.transaction
    || payload?.payment
    || payload?.result
    || payload
    || {};

  return {
    lotusTransactionId: normalizeLotusTransactionId(findNestedValue(source, [
      'transaction_id',
      'transactionId',
      'payment_id',
      'paymentId',
      'id'
    ])),
    lotusTransactionStatus: String(findNestedValue(source, [
      'status',
      'payment_status',
      'paymentStatus',
      'state',
      'result',
      'transaction_status',
      'transactionStatus'
    ])).trim(),
    lotusTransactionMessage: String(findNestedValue(payload, [
      'message',
      'detail',
      'description',
      'response_message',
      'responseMessage'
    ])).trim(),
    paymentTag: String(findNestedValue(source, [
      'payment_tag',
      'paymentTag',
      'tag',
      'reference',
      'payment_reference',
      'paymentReference'
    ])).trim()
  };
};

const paymentStatusTone = (value) => {
  const normalized = normalizePaymentStatus(value).toLowerCase();

  if (normalized === 'paid' || normalized === 'processed' || normalized === 'completed') {
    return 'success';
  }

  if (normalized === 'failed' || normalized === 'rejected') {
    return 'danger';
  }

  if (normalized === 'processing' || normalized === 'pending') {
    return 'warning';
  }

  return 'muted';
};

const buildEmptyPaymentForm = () => ({
  amount: '',
  paymentDate: getDateTimeInputValue(),
  method: SALARY_METHOD_OPTIONS[0],
  reference: '',
  notes: ''
});

const normalizeLotusStatusValue = (value) => String(value || '').trim().toLowerCase();

const mapLotusStatusToPayrollStatus = (value) => {
  const normalized = normalizeLotusStatusValue(value);

  if (LOTUS_SUCCESS_STATUSES.has(normalized)) {
    return 'Paid';
  }

  if (LOTUS_FAILED_STATUSES.has(normalized)) {
    return 'Failed';
  }

  return null;
};

const getPaymentDestinationDetails = (employee, salaryMethod) => {
  if (salaryMethod === 'MPESA') {
    return {
      recipientName: employee.employeeName || '',
      phoneNumber: String(employee.employeePhone || employee.phoneNumber || employee.phone || '').trim()
    };
  }

  if (salaryMethod === 'Bank Transfer') {
    return {
      recipientName: employee.employeeName || '',
      bankId: String(employee.salaryPaymentBankId || '').trim(),
      bankName: String(employee.salaryPaymentBankName || '').trim(),
      bankCode: String(employee.salaryPaymentBankCode || '').trim(),
      bankAccountNumber: String(employee.salaryPaymentReference || '').trim()
    };
  }

  return null;
};

function PaySalariesComp({ cashOnly = false }) {
  const navigate = useNavigate();
  const auth = getAuth();
  const [employees, setEmployees] = useState([]);
  const [payments, setPayments] = useState([]);
  const [periodKey, setPeriodKey] = useState(getMonthKey());
  const [statusFilter, setStatusFilter] = useState('Active');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [employeesLoaded, setEmployeesLoaded] = useState(false);
  const [paymentsLoaded, setPaymentsLoaded] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employeePickerTerm, setEmployeePickerTerm] = useState('');
  const [paymentForm, setPaymentForm] = useState(buildEmptyPaymentForm());

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setEmployeesLoaded(false);
    setPaymentsLoaded(false);
    try {
      const [employeeSnapshot, paymentSnapshot] = await Promise.all([
        getDocs(collection(db, 'Employees')),
        getDocs(collection(db, 'SalaryPayments'))
      ]);

      setEmployees(employeeSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setEmployeesLoaded(true);
      setPayments(paymentSnapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
      setPaymentsLoaded(true);
    } catch (error) {
      console.error('Failed to load pay salaries page:', error);
      swal('Error', 'Payroll could not be loaded right now.', 'error');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (employeesLoaded && paymentsLoaded) {
      setLoading(false);
    }
  }, [employeesLoaded, paymentsLoaded]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'SalaryPayments'),
      (snapshot) => {
        setPayments(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setPaymentsLoaded(true);
      },
      (error) => {
        console.error('Failed to subscribe to salary payments:', error);
        swal('Error', 'Live payroll updates are unavailable right now.', 'error');
      }
    );

    return unsubscribe;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const reconcilePendingPayments = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || payments.length === 0) {
        return;
      }

      const pendingPayments = payments.filter((payment) => {
        const status = normalizePaymentStatus(payment.paymentStatus || payment.status);
        return Boolean(payment.lotusTransactionId) && (status === 'pending' || status === 'processing' || status === 'submitted');
      });

      if (pendingPayments.length === 0) {
        return;
      }

      try {
        const idToken = await currentUser.getIdToken();
        const reconciledAt = Timestamp.now();

        await Promise.all(pendingPayments.map(async (payment) => {
          const response = await fetch(`${LOTUS_API_BASE_URL}/payments/${payment.lotusTransactionId}`, {
            headers: {
              Authorization: `Bearer ${idToken}`
            }
          });

          const payload = await response.json().catch(() => null);
      if (!response.ok) {
        return;
      }

      const lotusPaymentData = payload?.data?.transaction || payload?.data || payload || {};
      const lotusPaymentDetails = extractLotusPaymentDetails(payload);
      const lotusStatusRaw = lotusPaymentDetails.lotusTransactionStatus;
      const payrollStatus = mapLotusStatusToPayrollStatus(lotusStatusRaw);

      if (!payrollStatus || normalizePaymentStatus(payment.paymentStatus || payment.status).toLowerCase() === payrollStatus.toLowerCase()) {
        return;
      }

          const paymentRef = doc(db, 'SalaryPayments', payment.id);
          await setDoc(paymentRef, {
            paymentStatus: payrollStatus,
            status: payrollStatus,
            lotusTransactionStatus: lotusStatusRaw || payrollStatus,
            lotusPaymentResponse: lotusPaymentData,
            lotusReconciledAt: reconciledAt,
            lotusReconciledSource: 'polling'
          }, { merge: true });

          if (!cancelled) {
            setPayments((currentPayments) => currentPayments.map((item) => (
              item.id === payment.id
                ? {
                    ...item,
                    paymentStatus: payrollStatus,
                    status: payrollStatus,
                    lotusTransactionStatus: lotusStatusRaw || payrollStatus,
                    lotusPaymentResponse: lotusPaymentData,
                    lotusReconciledAt: reconciledAt,
                    lotusReconciledSource: 'polling'
                  }
                : item
            )));
          }
        }));
      } catch (error) {
        console.error('Failed to reconcile Lotus payment statuses:', error);
      }
    };

    reconcilePendingPayments();
    const intervalId = window.setInterval(reconcilePendingPayments, 90000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [auth, payments]);

  const payrollRows = useMemo(() => buildPayrollRows({
    employees,
    payments,
    periodKey
  }), [employees, payments, periodKey]);

  const selectedPayrollEmployee = useMemo(() => (
    payrollRows.find((employee) => employee.id === selectedEmployeeId) || null
  ), [payrollRows, selectedEmployeeId]);

  const paymentAllocationPreview = useMemo(() => {
    if (!selectedPayrollEmployee) return [];

    return buildPayrollAllocationPreview({
      timeline: selectedPayrollEmployee.timeline,
      amount: Number(paymentForm.amount || 0)
    });
  }, [paymentForm.amount, selectedPayrollEmployee]);

  const paymentLedgerRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...payments]
      .sort((first, second) => getPaymentTimestamp(second) - getPaymentTimestamp(first))
      .filter((payment) => {
        if (cashOnly && String(payment.method || '').toLowerCase() !== 'cash') return false;
        const employeeStatus = normalizeEmployeeStatus(payment.employeeStatus);
        const matchesStatus = statusFilter === 'All' || employeeStatus === statusFilter;
        if (!matchesStatus) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const searchBlob = [
          payment.employeeName,
          payment.salaryPeriodLabel,
          payment.method,
          payment.reference,
          payment.notes,
          payment.jobPosition,
          payment.campus
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchBlob.includes(normalizedSearch);
      });
  }, [cashOnly, payments, searchTerm, statusFilter]);

  const filteredPaymentsTotal = useMemo(() => {
    return paymentLedgerRows.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
  }, [paymentLedgerRows]);

  const currentMonthPaymentsTotal = useMemo(() => {
    return payments.reduce((sum, payment) => {
      return resolveSalaryPaymentPeriodKey(payment) === periodKey && isSuccessfulSalaryPayment(payment)
        ? sum + normalizeAmount(payment.amount)
        : sum;
    }, 0);
  }, [payments, periodKey]);

  const selectableEmployees = useMemo(() => {
    const normalizedSearch = employeePickerTerm.trim().toLowerCase();

    return payrollRows
      .filter((employee) => normalizeEmployeeStatus(employee.employeeStatus) !== 'Archived')
      .filter((employee) => {
        if (!normalizedSearch) return true;

        const searchBlob = [
          employee.employeeName,
          employee.jobPosition,
          employee.campus,
          employee.employeeEmail
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return searchBlob.includes(normalizedSearch);
      });
  }, [employeePickerTerm, payrollRows]);

  const openPaymentModal = (employeeId = '') => {
    const targetEmployee = payrollRows.find((employee) => employee.id === employeeId)
      || payrollRows.find((employee) => employee.totalOutstanding > 0)
      || payrollRows[0]
      || null;

    setSelectedEmployeeId(targetEmployee?.id || '');
    setEmployeePickerTerm('');
    setPaymentForm({
      ...buildEmptyPaymentForm(),
      method: cashOnly ? 'Cash' : targetEmployee?.salaryPaymentMethod || SALARY_METHOD_OPTIONS[0]
    });
    setModalShow(true);
  };

  const closePaymentModal = () => {
    if (submittingPayment) return;
    setModalShow(false);
  };

  const handleSelectedEmployeeChange = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    const employee = payrollRows.find((item) => item.id === employeeId);
    setPaymentForm((currentForm) => ({
      ...currentForm,
      amount: '',
      reference: '',
      notes: '',
      method: cashOnly ? 'Cash' : employee?.salaryPaymentMethod || currentForm.method || SALARY_METHOD_OPTIONS[0]
    }));
  };

  const handlePaymentFormChange = (event) => {
    const { name, value } = event.target;
    setPaymentForm((currentForm) => ({
      ...currentForm,
      [name]: value
    }));
  };

  const applySuggestedAmount = (amount) => {
    setPaymentForm((currentForm) => ({
      ...currentForm,
      amount: String(normalizeAmount(amount))
    }));
  };

  const saveSalaryPayment = async () => {
    if (!selectedPayrollEmployee) {
      swal('Choose employee', 'Select an employee before making a payment.', 'warning');
      return;
    }

    const amountValue = Number(paymentForm.amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      swal('Invalid amount', 'Payment amount should be greater than zero.', 'warning');
      return;
    }

    if (selectedPayrollEmployee.totalOutstanding <= 0) {
      swal('No balance', 'This employee has no outstanding salary balance for the selected scope.', 'info');
      return;
    }

    if (amountValue > selectedPayrollEmployee.totalOutstanding) {
      swal(
        'Amount too high',
        `You can only pay up to ${formatKes(selectedPayrollEmployee.totalOutstanding)} for ${selectedPayrollEmployee.employeeName || 'this employee'}.`,
        'warning'
      );
      return;
    }

    const paymentDateValue = new Date(paymentForm.paymentDate);
    if (Number.isNaN(paymentDateValue.getTime())) {
      swal('Invalid date', 'Please choose a valid payment date.', 'warning');
      return;
    }

    if (paymentAllocationPreview.length === 0) {
      swal('No allocation', 'There is no unpaid month to allocate this payment to.', 'warning');
      return;
    }

    const salaryMethod = paymentForm.method || selectedPayrollEmployee.salaryPaymentMethod || SALARY_METHOD_OPTIONS[0];
    const currentUser = auth.currentUser;
    if (!currentUser) {
      swal('Session expired', 'Please sign in again before submitting a salary payment.', 'warning');
      return;
    }

    const paymentTimestamp = Timestamp.fromDate(paymentDateValue);
    const createdAt = Timestamp.now();
    const allocationBreakdown = paymentAllocationPreview.map((allocation, index) => ({
      periodKey: allocation.periodKey,
      periodLabel: allocation.periodLabel,
      expectedAmount: allocation.expectedAmount,
      allocatedAmount: allocation.allocatedAmount,
      allocationOrder: index + 1
    }));

    if (salaryMethod === 'Cash') {
      setSubmittingPayment(true);
      try {
        const paymentRef = doc(collection(db, 'SalaryPayments'));
        const transactionGroupId = `salary-cash-${paymentRef.id}`;
        const paymentPayload = {
          employeeId: selectedPayrollEmployee.id,
          employeeName: selectedPayrollEmployee.employeeName || '',
          employeeEmail: selectedPayrollEmployee.employeeEmail || selectedPayrollEmployee.currentEmail || '',
          employeePhone: selectedPayrollEmployee.employeePhone || selectedPayrollEmployee.phoneNumber || '',
          campus: selectedPayrollEmployee.campus || '',
          jobPosition: selectedPayrollEmployee.jobPosition || '',
          employeeStatus: selectedPayrollEmployee.employeeStatus || 'Active',
          amount: amountValue,
          paymentDate: paymentTimestamp,
          method: 'Cash',
          paymentStatus: 'Paid',
          status: 'Paid',
          reference: paymentForm.reference.trim(),
          notes: paymentForm.notes.trim(),
          salaryPeriodKey: periodKey,
          salaryPeriodLabel: getMonthLabel(periodKey),
          expectedSalary: selectedPayrollEmployee.expectedAmount,
          createdAt,
          createdBy: currentUser.uid,
          docId: paymentRef.id,
          transactionGroupId,
          selectedPayrollMonth: periodKey,
          totalOutstandingAtPayment: selectedPayrollEmployee.totalOutstanding,
          previousOutstandingAtPayment: selectedPayrollEmployee.previousOutstanding,
          currentMonthOutstandingAtPayment: selectedPayrollEmployee.currentBucket.remainingAmount,
          allocationBreakdown,
          paymentDestinationSummary: { method: 'Cash', detail: 'Paid in cash' },
          paymentProvider: 'Manual',
          sourcePage: 'pay-salaries'
        };

        await setDoc(paymentRef, paymentPayload);
        await logAdminActivity({
          action: 'Recorded Cash Salary Payment',
          details: `Recorded ${formatKes(amountValue)} in cash for ${selectedPayrollEmployee.employeeName || 'employee'} from payroll page`,
          meta: {
            employeeId: selectedPayrollEmployee.id,
            employeeName: selectedPayrollEmployee.employeeName || '',
            employeeSection: 'salaryPayments',
            paymentTransactionGroupId: transactionGroupId,
            paymentCount: 1
          }
        });

        setPayments((currentPayments) => upsertSalaryPayment(currentPayments, { id: paymentRef.id, ...paymentPayload }));
        setModalShow(false);
        swal('Cash payment recorded', `${formatKes(amountValue)} recorded as paid in cash.`, 'success');
      } catch (error) {
        console.error('Failed to record cash salary payment:', error);
        swal('Error', error.message || 'The cash salary payment could not be recorded.', 'error');
      } finally {
        setSubmittingPayment(false);
      }
      return;
    }

    const lotusPaymentMethod = LOTUS_METHOD_BY_SALARY_METHOD[salaryMethod];
    if (!lotusPaymentMethod) {
      swal(
        'Unsupported method',
        'Lotus only processes bank transfers and MPESA payments. Please change the employee payment method first.',
        'warning'
      );
      return;
    }

    const destinationDetails = getPaymentDestinationDetails(selectedPayrollEmployee, salaryMethod);
    if (!destinationDetails) {
      swal('Missing payout details', 'We could not prepare the payout destination for this employee.', 'warning');
      return;
    }

    if (lotusPaymentMethod === 'pesalink') {
      if (!destinationDetails.bankId || !destinationDetails.bankAccountNumber) {
        swal(
          'Missing bank details',
          'Add the employee bank name and bank account number in Payroll and compliance before submitting this payment.',
          'warning'
        );
        return;
      }
    }

    if (lotusPaymentMethod === 'mpesa' && !destinationDetails.phoneNumber) {
      swal(
        'Missing phone number',
        'Add the employee phone number before submitting an MPESA payment.',
        'warning'
      );
      return;
    }

    setSubmittingPayment(true);
    try {
      const idToken = await currentUser.getIdToken();
      const transactionGroupId = generateLotusPaymentTag();
      const lotusPayload = {
        amount: amountValue,
        payment_method: lotusPaymentMethod,
        recipient_name: destinationDetails.recipientName || selectedPayrollEmployee.employeeName || 'Employee',
        payment_purpose: `Salary payment for ${selectedPayrollEmployee.employeeName || 'employee'} - ${getMonthLabel(periodKey)}`,
        payment_tag: transactionGroupId,
        payment_category: 'payroll'
      };

      if (lotusPaymentMethod === 'pesalink') {
        lotusPayload.bank_id = destinationDetails.bankId;
        lotusPayload.bank_account_number = destinationDetails.bankAccountNumber;
      }

      if (lotusPaymentMethod === 'mpesa') {
        lotusPayload.phone_number = destinationDetails.phoneNumber;
      }

      const lotusResponse = await fetch(`${LOTUS_API_BASE_URL}/payments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(lotusPayload)
      });

      const lotusResponseBody = await lotusResponse.json().catch(() => null);
      if (!lotusResponse.ok) {
        const lotusErrorMessage = lotusResponseBody?.error?.message
          || lotusResponseBody?.error?.detail
          || lotusResponseBody?.error
          || lotusResponseBody?.message
          || 'Lotus rejected the payment request.';
        throw new Error(typeof lotusErrorMessage === 'string' ? lotusErrorMessage : 'Lotus rejected the payment request.');
      }

      const lotusPaymentDetails = extractLotusPaymentDetails(lotusResponseBody);
      const lotusPaymentData = lotusResponseBody?.data?.transaction
        || lotusResponseBody?.data
        || lotusResponseBody
        || {};
      const lotusTransactionId = lotusPaymentDetails.lotusTransactionId;
      const lotusTransactionStatus = lotusPaymentDetails.lotusTransactionStatus || 'pending';
      const lotusTransactionMessage = lotusPaymentDetails.lotusTransactionMessage
        || lotusResponseBody?.message
        || 'Payment submitted to Lotus.';

      const paymentRef = doc(collection(db, 'SalaryPayments'));
      const paymentPayload = {
        employeeId: selectedPayrollEmployee.id,
        employeeName: selectedPayrollEmployee.employeeName || '',
        employeeEmail: selectedPayrollEmployee.employeeEmail || selectedPayrollEmployee.currentEmail || '',
        employeePhone: selectedPayrollEmployee.employeePhone || selectedPayrollEmployee.phoneNumber || '',
        campus: selectedPayrollEmployee.campus || '',
        jobPosition: selectedPayrollEmployee.jobPosition || '',
        employeeStatus: selectedPayrollEmployee.employeeStatus || 'Active',
        amount: amountValue,
        paymentDate: paymentTimestamp,
        method: paymentForm.method,
        paymentStatus: 'Pending',
        reference: paymentForm.reference.trim(),
        notes: paymentForm.notes.trim(),
        salaryPeriodKey: periodKey,
        salaryPeriodLabel: getMonthLabel(periodKey),
        expectedSalary: selectedPayrollEmployee.expectedAmount,
        createdAt,
        createdBy: currentUser.uid,
        docId: paymentRef.id,
        transactionGroupId,
        lotusPaymentTag: transactionGroupId,
        selectedPayrollMonth: periodKey,
        totalOutstandingAtPayment: selectedPayrollEmployee.totalOutstanding,
        previousOutstandingAtPayment: selectedPayrollEmployee.previousOutstanding,
        currentMonthOutstandingAtPayment: selectedPayrollEmployee.currentBucket.remainingAmount,
        allocationBreakdown,
        lotusPaymentMethod,
        lotusTransactionId,
        lotusTransactionStatus,
        lotusTransactionMessage,
        lotusPaymentResponse: lotusPaymentData,
        paymentDestinationSummary: lotusPaymentMethod === 'pesalink'
          ? {
              method: salaryMethod,
              bankId: destinationDetails.bankId,
              bankName: destinationDetails.bankName,
              bankCode: destinationDetails.bankCode,
              bankAccountLast4: destinationDetails.bankAccountNumber.slice(-4)
            }
          : {
              method: salaryMethod,
              phoneLast4: destinationDetails.phoneNumber.slice(-4)
            },
        sourcePage: 'pay-salaries'
      };

      await setDoc(paymentRef, paymentPayload);
      const createdPayment = { id: paymentRef.id, ...paymentPayload };

      await logAdminActivity({
        action: 'Recorded Salary Payment',
        details: `Recorded ${formatKes(amountValue)} for ${selectedPayrollEmployee.employeeName || 'employee'} from payroll page`,
        meta: {
          employeeId: selectedPayrollEmployee.id,
          employeeName: selectedPayrollEmployee.employeeName || '',
          employeeSection: 'salaryPayments',
          paymentTransactionGroupId: transactionGroupId,
          paymentCount: 1,
          lotusTransactionId
        }
      });

      setPayments((currentPayments) => upsertSalaryPayment(currentPayments, createdPayment));
      setModalShow(false);
      swal(
        'Payment submitted',
        `Submitted ${formatKes(amountValue)} to Lotus and saved the payroll record as pending while the transfer processes.`,
        'success'
      );
    } catch (error) {
      console.error('Failed to save salary payment:', error);
      swal('Error', error.message || 'The salary payment could not be submitted to Lotus.', 'error');
    } finally {
      setSubmittingPayment(false);
    }
  };

  return (
    <div className="clientsComp incomePage paySalariesPage">
      <section className="incomeHero paySalariesHero">
        <div className="incomeHeroCopy">
          <div className="incomeHeroTitleRow">
            <h5>{cashOnly ? 'Cash Salary Payments' : 'Pay Salaries'}</h5>
          </div>
          <p className="paySalariesHeroText">
            {cashOnly
              ? 'Record salaries already paid in cash and keep them in the payroll payment register.'
              : 'Record salary payments from one modal and use this page as the payroll payment register.'}
          </p>
          <p className="incomeHeaderMeta">
            <span>Payment month {getMonthLabel(periodKey)}</span>
            <span className="incomeInlineDot" />
            <span>{paymentLedgerRows.length} {cashOnly ? 'cash ' : ''}payment records</span>
            <span className="incomeInlineDot" />
            <span>{formatKes(filteredPaymentsTotal)} in the table</span>
          </p>
        </div>
        <div className="incomeHeroActions paySalariesHeroActions">
          <button type="button" className="btn btn-dark" onClick={() => openPaymentModal()}>
            <FontAwesomeIcon icon={faMoneyCheckDollar} /> {cashOnly ? 'Record cash payment' : 'Initiate payment'}
          </button>
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={() => navigate('/payroll-ledger')}
          >
            Open payroll ledger
          </button>
        </div>
      </section>

      {loading ? (
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading salary payments...</p>
        </div>
      ) : (
        <>
          <div className="searchContainer incomeToolbar paySalariesToolbar">
            <div className="paySalariesFilters">
              <Form.Control
                type="month"
                value={periodKey}
                onChange={(event) => setPeriodKey(event.target.value)}
                className="paySalariesControl"
              />
              <Form.Control
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search employee, method, reference, notes..."
                className="paySalariesControl paySalariesSearch"
              />
              <Form.Select
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                className="paySalariesControl"
              >
                <option value="All">All statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Archived">Archived</option>
              </Form.Select>
            </div>
            <div className="incomeToolbarMeta">
              <span className="incomeToolbarText">
                {getMonthLabel(periodKey)} allocations total {formatKes(currentMonthPaymentsTotal)}
              </span>
            </div>
          </div>

          <article className="incomeTableCard paySalariesMainCard paySalariesLedgerCard">
            <div className="incomeTableHeader">
              <div>
                <h6>Salary payment records</h6>
                <p>Use Payroll ledger from the HR menu to review balances and employee payment snapshots.</p>
              </div>
            </div>

            {paymentLedgerRows.length === 0 ? (
              <div className="incomeStateCard incomeEmptyState paySalariesEmptyState">
                <h6>No salary payments recorded</h6>
                <p>The payment ledger will fill up here after the first payroll transaction is saved.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle incomeRecordsTable paySalariesTable paySalariesLedgerTable">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Applied month</th>
                      <th>Paid on</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLedgerRows.map((payment) => (
                      <tr key={payment.id}>
                        <td>
                          <div className="paySalariesEmployeeCell">
                            <button
                              type="button"
                              className="paySalariesEmployeeLink"
                              onClick={() => navigate(`/view-employee/${payment.employeeId}`)}
                            >
                              {payment.employeeName || 'Employee'}
                            </button>
                            <span>{payment.jobPosition || 'Role not set'} • {payment.campus || 'Campus not set'}</span>
                          </div>
                        </td>
                        <td>{payment.salaryPeriodLabel || getMonthLabel(resolveSalaryPaymentPeriodKey(payment))}</td>
                        <td>{formatDateTime(payment.paymentDate)}</td>
                        <td><span className="paySalariesAmountText">{formatKes(payment.amount)}</span></td>
                        <td>{payment.method || '—'}</td>
                        <td>
                          <span className={`opsStatusPill opsStatusPill--${paymentStatusTone(payment.paymentStatus || payment.status)}`}>
                            {normalizePaymentStatus(payment.paymentStatus || payment.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        </>
      )}

      <Modal show={modalShow} onHide={closePaymentModal} centered size="lg" contentClassName="paySalariesModalContent">
        <Modal.Header closeButton={!submittingPayment}>
          <Modal.Title>{cashOnly ? 'Record cash salary payment' : 'Record salary payment'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="paySalariesModalIntro">
            <p>
              Select an employee, review the selected month balance and previous unpaid months, then record one payment.
              The system will allocate it oldest-first and continue into {getMonthLabel(periodKey)} if there is balance left.
            </p>
          </div>

          <div className="paySalariesModalControls">
            <Form.Group>
              <Form.Label>Search employee</Form.Label>
              <Form.Control
                type="search"
                value={employeePickerTerm}
                onChange={(event) => setEmployeePickerTerm(event.target.value)}
                placeholder="Search employee, role, campus..."
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Select employee</Form.Label>
              <Form.Select
                value={selectedEmployeeId}
                onChange={(event) => handleSelectedEmployeeChange(event.target.value)}
              >
                <option value="">Choose employee</option>
                {selectableEmployees.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.employeeName || 'Employee'} • {employee.jobPosition || 'Role not set'} • {formatKes(employee.totalOutstanding)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>

          {!selectedPayrollEmployee ? (
            <div className="paySalariesModalState">
              <h6>Select an employee to continue</h6>
              <p>Payroll balance details and payment allocation preview will appear here once you choose a staff member.</p>
            </div>
          ) : (
            <>
              <div className="paySalariesModalSummary">
                <div className="paySalariesModalMetric">
                  <span>{getMonthLabel(periodKey)} balance</span>
                  <strong>{formatKes(selectedPayrollEmployee.currentBucket.remainingAmount)}</strong>
                </div>
                <div className="paySalariesModalMetric">
                  <span>Previous months total</span>
                  <strong>{formatKes(selectedPayrollEmployee.previousOutstanding)}</strong>
                </div>
                <div className="paySalariesModalMetric">
                  <span>Total payable now</span>
                  <strong>{formatKes(selectedPayrollEmployee.totalOutstanding)}</strong>
                </div>
                <div className="paySalariesModalMetric">
                  <span>Unpaid months</span>
                  <strong>{selectedPayrollEmployee.outstandingPeriods}</strong>
                </div>
              </div>

                <div className="paySalariesModalEmployeeCard">
                <strong>{selectedPayrollEmployee.employeeName || 'Employee'}</strong>
                <span>{selectedPayrollEmployee.jobPosition || 'Role not set'} • {selectedPayrollEmployee.campus || 'Campus not set'}</span>
                <small>
                  Salary {formatKes(selectedPayrollEmployee.expectedAmount)} • Payment method {selectedPayrollEmployee.salaryPaymentMethod || 'Not set'}
                </small>
              </div>

              <div className="paySalariesModalState paySalariesModalState--compact">
                <h6>{paymentForm.method === 'Cash' ? 'Cash payment' : 'Payout destination'}</h6>
                {paymentForm.method === 'Cash' ? (
                  <p>This records a cash payment that has already been paid and marks it as paid in payroll.</p>
                ) : paymentForm.method === 'Bank Transfer' ? (
                  <p>
                    {selectedPayrollEmployee.salaryPaymentBankName || 'Bank not set'}
                    {selectedPayrollEmployee.salaryPaymentBankCode ? ` • ${selectedPayrollEmployee.salaryPaymentBankCode}` : ''}
                    {' '}• Account {selectedPayrollEmployee.salaryPaymentReference || 'not set'}
                  </p>
                ) : paymentForm.method === 'MPESA' ? (
                  <p>MPESA number {selectedPayrollEmployee.employeePhone || selectedPayrollEmployee.phoneNumber || 'not set'}</p>
                ) : (
                  <p>This payment method is not supported for electronic payout.</p>
                )}
              </div>

              <div className="paySalariesModalForm">
                <Form.Group className="mb-3">
                  <Form.Label>Payment amount</Form.Label>
                  <Form.Control
                    type="number"
                    name="amount"
                    value={paymentForm.amount}
                    onChange={handlePaymentFormChange}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    max={selectedPayrollEmployee.totalOutstanding || undefined}
                  />
                  <div className="paySalariesModalQuickActions">
                    <button
                      type="button"
                      className="btn btn-outline-dark btn-sm"
                      onClick={() => applySuggestedAmount(selectedPayrollEmployee.currentBucket.remainingAmount)}
                    >
                      Use current month balance
                    </button>
                    <button
                      type="button"
                      className="btn btn-dark btn-sm"
                      onClick={() => applySuggestedAmount(selectedPayrollEmployee.totalOutstanding)}
                    >
                      Clear full balance
                    </button>
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Payment date</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="paymentDate"
                    value={paymentForm.paymentDate}
                    onChange={handlePaymentFormChange}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Payment method</Form.Label>
                  <Form.Select name="method" value={cashOnly ? 'Cash' : paymentForm.method} onChange={handlePaymentFormChange} disabled={cashOnly}>
                    {(cashOnly ? ['Cash'] : SALARY_METHOD_OPTIONS).map((method) => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Receipt or reference <span className="text-muted">(optional)</span></Form.Label>
                  <Form.Control
                    name="reference"
                    value={paymentForm.reference}
                    onChange={handlePaymentFormChange}
                    placeholder={paymentForm.method === 'Cash' ? 'Receipt number' : 'Payment reference'}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Notes <span className="text-muted">(optional)</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="notes"
                    value={paymentForm.notes}
                    onChange={handlePaymentFormChange}
                    placeholder={paymentForm.method === 'Cash' ? 'Add any cash payment details' : 'Add a note'}
                  />
                </Form.Group>
              </div>

              <div className="paySalariesModalAllocation">
                <div className="paySalariesModalAllocationHeader">
                  <div>
                    <h6>Allocation preview</h6>
                    <p>This payment will be distributed oldest-first so historical arrears stay accurate.</p>
                  </div>
                  <span>{paymentAllocationPreview.length} month{paymentAllocationPreview.length === 1 ? '' : 's'}</span>
                </div>

                {paymentAllocationPreview.length === 0 ? (
                  <div className="paySalariesModalState paySalariesModalState--compact">
                    <p>Enter a valid amount to preview how the payment will be allocated.</p>
                  </div>
                ) : (
                  <div className="paySalariesModalAllocationList">
                    {paymentAllocationPreview.map((allocation) => (
                      <div key={allocation.periodKey} className="paySalariesModalAllocationRow">
                        <div>
                          <strong>{allocation.periodLabel}</strong>
                          <span>Outstanding before payment {formatKes(allocation.remainingAmount)}</span>
                        </div>
                        <div className="paySalariesRecentAside">
                          <strong>{formatKes(allocation.allocatedAmount)}</strong>
                          <span>Applied now</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePaymentModal} disabled={submittingPayment}>
            Close
          </Button>
          <Button variant="dark" onClick={saveSalaryPayment} disabled={submittingPayment || !selectedPayrollEmployee}>
            {submittingPayment ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              paymentForm.method === 'Cash' ? 'Record cash payment' : 'Submit to Lotus'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PaySalariesComp;
